import { LeavingSoon } from "@/components/GoingHomeLeavingSoon";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RefreshCw, AlertCircle, DogIcon, Clock, User, Search, SortAsc, Tv, Users, Bell, MessageSquare, Send, X, Settings, Scale, Flag, Repeat, PackageX, AlertTriangle, Tag, ChevronDown } from "lucide-react";
import { StandingBadge } from "../components/StandingBadge";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Size filter options
const SIZE_FILTERS = {
  all: { label: "All Dogs", icon: "🐕" },
  small: { label: "Small (<35 lb)", icon: "🐕" },
  large: { label: "Large (≥36 lb)", icon: "🐕‍🦺" }
};

// Icon component for displaying Gingr custom flags/icons
// Colors for playgroup icons are overridden to match the app's size badge colors
const GingrIcon = ({ icon }) => {
  const { title, color: originalColor } = icon;
  
  // Override colors for playgroup icons to match our S/L badge colors
  // Small = Blue (#3b82f6 = bg-blue-500), Large = Green (#22c55e = bg-green-500)
  let displayColor = originalColor;
  const titleLower = title?.toLowerCase() || '';
  
  if (titleLower.includes('small') && titleLower.includes('playgroup')) {
    displayColor = '#3b82f6'; // Tailwind blue-500 to match S badge
  } else if (titleLower.includes('large') && titleLower.includes('playgroup')) {
    displayColor = '#22c55e'; // Tailwind green-500 to match L badge
  }
  
  return (
    <div 
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ backgroundColor: displayColor, color: '#fff' }}
      title={title}
      data-testid={`gingr-icon-${title?.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Flag className="w-3 h-3" />
      <span className="max-w-[80px] truncate">{title}</span>
    </div>
  );
};

// Daycare package renewal banner shown on the pickup card (front-desk trigger)
const PackageRenewalBanner = ({ status }) => {
  if (!status || !status.is_alert) return null;
  const s = status.status;
  const cfg = {
    out: { cls: "bg-rose-50 border-rose-200 text-rose-800", Icon: PackageX, title: "Package empty — sell renewal",
           desc: "Daycare credits used up. Suggest a new package." },
    low: { cls: "bg-amber-50 border-amber-200 text-amber-800", Icon: AlertTriangle, title: `${status.daycare_remaining} credit${status.daycare_remaining === 1 ? "" : "s"} left`,
           desc: "Daycare package almost done — good time to mention renewal." },
    no_package: { cls: "bg-sky-50 border-sky-200 text-sky-800", Icon: Tag, title: "No package — offer one",
           desc: status.recent_daycare_visits ? `${status.recent_daycare_visits} paid visit${status.recent_daycare_visits === 1 ? "" : "s"} in 90 days. A package saves money.` : "Pays per visit. A package saves money." },
  }[s];
  if (!cfg) return null;
  const { Icon } = cfg;
  return (
    <div className={`flex items-start gap-2.5 rounded-xl border px-3 py-2.5 ${cfg.cls}`} data-testid={`renewal-banner-${s}`}>
      <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
      <div className="min-w-0">
        <div className="text-sm font-bold leading-tight">{cfg.title}</div>
        <div className="text-xs leading-snug opacity-90">{cfg.desc}</div>
      </div>
    </div>
  );
};


function GoingHome() {
  const navigate = useNavigate();
  const [animals, setAnimals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastSynced, setLastSynced] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name"); // owner, name, time
  const [sizeFilter, setSizeFilter] = useState("all"); // all, small, large
  const [notifying, setNotifying] = useState(null); // Track which owner is being notified
  
  // Toggle to show/hide Private Play and Day Boarding dogs
  const [showSpecialCategories, setShowSpecialCategories] = useState(false);
  
  // Message modal state
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [customMessage, setCustomMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageTarget, setMessageTarget] = useState("both"); // "large", "small", "both"
  
  // Gingr configuration status
  const [gingrConfigured, setGingrConfigured] = useState(true);
  const [gingrMessage, setGingrMessage] = useState("");
  const [currentLocationId, setCurrentLocationId] = useState("");
  
  // TV connection status
  const [tvConnected, setTvConnected] = useState(null); // null = checking, true = connected, false = not connected
  const [tvCount, setTvCount] = useState(0);
  const disconnectCountRef = useRef(0);
  
  // Category counts
  const [smallCount, setSmallCount] = useState(0);
  const [largeCount, setLargeCount] = useState(0);
  const [privatePlayCount, setPrivatePlayCount] = useState(0);
  const [packageMap, setPackageMap] = useState(() => {
    // SWR: show last-known package banners instantly (like the dog list) so they
    // don't flicker in on every load / poll.
    try {
      const loc = localStorage.getItem('currentLocationId') || '';
      const cached = localStorage.getItem(`swr_pkgmap_${loc}`);
      return cached ? JSON.parse(cached) : {};
    } catch (e) { return {}; }
  }); // owner_id -> renewal status
  const [dayBoardingCount, setDayBoardingCount] = useState(0);
  const [standingCount, setStandingCount] = useState(0);

  // Check TV connection status with debounce to prevent flickering
  const checkTvStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const locationId = localStorage.getItem('currentLocationId');
      const response = await axios.get(`${API}/going-home/ws-status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Location-Id': locationId
        }
      });
      const connections = response.data.connections_by_location || {};
      const count = connections[locationId] || 0;
      
      if (count > 0) {
        // Connected - show immediately (good news is instant)
        disconnectCountRef.current = 0;
        setTvCount(count);
        setTvConnected(true);
      } else {
        // Disconnected - only update after 3 consecutive failures to prevent flickering
        disconnectCountRef.current += 1;
        if (disconnectCountRef.current >= 3) {
          setTvCount(0);
          setTvConnected(false);
        }
      }
    } catch (error) {
      disconnectCountRef.current += 1;
      if (disconnectCountRef.current >= 3) {
        setTvConnected(false);
      }
    }
  };

  // Poll TV status every 2 seconds (debounced, so 3 failures = 6s before showing disconnected)
  useEffect(() => {
    checkTvStatus();
    const interval = setInterval(checkTvStatus, 2000);
    return () => clearInterval(interval);
  }, [currentLocationId]);

  const [ghLocId] = useState(() => localStorage.getItem('currentLocationId') || '');
  
  useEffect(() => {
    setCurrentLocationId(ghLocId);
    
    // SWR: show cached going home data immediately
    try {
      const cached = localStorage.getItem(`swr_goinghome_${ghLocId}`);
      if (cached) {
        const data = JSON.parse(cached);
        setAnimals(data.animals || []);
        setGingrConfigured(data.gingr_configured !== false);
        setSmallCount(data.small_count || 0);
        setLargeCount(data.large_count || 0);
        setPrivatePlayCount(data.private_play_count || 0);
        setDayBoardingCount(data.day_boarding_count || 0);
        setStandingCount(data.standing_count || 0);
        setLoading(false);
      }
    } catch(e) {}
    
    fetchGoingHome();
    
    const interval = setInterval(fetchGoingHome, 30 * 1000);
    return () => clearInterval(interval);
  }, [ghLocId]);

  // Fetch daycare package renewal status (owner_id -> status) for pickup banners.
  //
  // TWO SOURCES, sticky-merged (never clear a known-good status):
  //   1) INSTANT: seed from `animal.package_status` inlined by the backend on
  //      /api/going-home. This renders the banner in the SAME paint frame as
  //      the dog card — zero visible lag between the dog's name and the flag.
  //   2) BACKGROUND refresh: POST /package-credits/going-home every 5 min so
  //      newly-flagged owners (or newly-purchased packages) roll in without a
  //      hard refresh.
  //
  // Once a status is set for an owner, we never overwrite it with an "unknown"
  // or absent response — the flag "sticks" until a full refresh (or the owner
  // buys a new package, which the background refresh picks up).
  useEffect(() => {
    // Reconcile from the inlined package_status on the animal objects. The
    // backend now emits package_status AUTHORITATIVELY (an alert dict, or an
    // explicit null when the owner is NOT an alert). So:
    //   • alert dict  → set/refresh the banner
    //   • explicit null → CLEAR any stale banner (owner bought a package)
    //   • key absent  → leave as-is (cache cold; keep prior sticky value)
    setPackageMap(prev => {
      const next = { ...prev };
      let changed = false;
      for (const a of animals) {
        const oid = a.owner_id != null ? String(a.owner_id) : null;
        if (!oid) continue;
        if (!('package_status' in a)) continue; // backend didn't compute → keep prior
        const st = a.package_status;
        if (st && st.status && st.status !== 'unknown') {
          const cur = next[oid];
          if (!cur || cur.status !== st.status || cur.daycare_remaining !== st.daycare_remaining) {
            next[oid] = st;
            changed = true;
          }
        } else if (next[oid]) {
          // Authoritative "not an alert" → drop the (now-resolved) banner.
          delete next[oid];
          changed = true;
        }
      }
      if (changed) {
        try {
          const loc = localStorage.getItem('currentLocationId') || '';
          localStorage.setItem(`swr_pkgmap_${loc}`, JSON.stringify(next));
        } catch (e) {}
      }
      return changed ? next : prev;
    });
  }, [animals]);

  useEffect(() => {
    let cancelled = false;
    const owners = [];
    const seen = new Set();
    for (const a of animals) {
      const oid = a.owner_id;
      if (oid && !seen.has(String(oid))) {
        seen.add(String(oid));
        owners.push({ owner_id: String(oid), owner_name: a.owner_name, owner_phone: a.owner_phone });
      }
    }
    if (owners.length === 0) { return; }
    const fetchPackages = async () => {
      try {
        const res = await axios.post(`${API}/package-credits/going-home`, { owners });
        if (!cancelled && res.data?.owners) {
          // Merge into the existing map and never overwrite a known-good status
          // with a transient "unknown" — this is what caused banners to blink
          // out and back in. Persist the merged map for instant SWR render.
          setPackageMap(prev => {
            const next = { ...prev };
            for (const [oid, st] of Object.entries(res.data.owners)) {
              if (st && st.status && st.status !== 'unknown') next[oid] = st;
            }
            try {
              const loc = localStorage.getItem('currentLocationId') || '';
              localStorage.setItem(`swr_pkgmap_${loc}`, JSON.stringify(next));
            } catch (e) {}
            return next;
          });
        }
      } catch (e) { /* non-fatal — banners just won't show */ }
    };
    fetchPackages();
    const iv = setInterval(fetchPackages, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [animals]);

  // Group animals by owner (filtering based on showSpecialCategories toggle)
  const ownerGroups = useMemo(() => {
    const groups = {};
    
    // Filter animals based on showSpecialCategories toggle
    // Note: If dog_category is not set (old cached data), treat as 'daycare' for backwards compatibility
    const filteredAnimals = showSpecialCategories 
      ? animals 
      : animals.filter(a => !a.dog_category || a.dog_category === 'daycare');
    
    filteredAnimals.forEach(animal => {
      // Group by OWNER ID (two different owners can share a last name — keying by
      // name would wrongly merge them). Fall back to name only if id is missing.
      const ownerKey = animal.owner_id != null ? String(animal.owner_id) : (animal.owner_name || 'Unknown Owner');
      if (!groups[ownerKey]) {
        groups[ownerKey] = {
          owner_key: ownerKey,
          owner_name: animal.owner_name || 'Unknown Owner',
          owner_id: animal.owner_id,
          owner_phone: animal.owner_phone,
          animals: [],
          checkout_time: animal.checkout_time
        };
      }
      groups[ownerKey].animals.push(animal);
      // Use earliest checkout time for the group
      const cur = groups[ownerKey].checkout_time;
      if (animal.checkout_time && animal.checkout_time !== "00:00" && (!cur || cur === "00:00" || animal.checkout_time < cur)) {
        groups[ownerKey].checkout_time = animal.checkout_time;
      }
    });
    
    return Object.values(groups);
  }, [animals, showSpecialCategories]);

  // Filter and sort owner groups
  const filteredGroups = useMemo(() => {
    let filtered = [...ownerGroups];
    
    // Apply size filter first - filter animals within each group
    if (sizeFilter !== 'all') {
      filtered = filtered.map(group => ({
        ...group,
        animals: group.animals.filter(animal => animal.size_category === sizeFilter)
      })).filter(group => group.animals.length > 0);
    }
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(group =>
        group.owner_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.animals.some(animal => 
          animal.animal_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (animal.breed && animal.breed.toLowerCase().includes(searchTerm.toLowerCase()))
        )
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "owner":
          return a.owner_name.localeCompare(b.owner_name);
        case "name":
          return a.animals[0].animal_name.localeCompare(b.animals[0].animal_name);
        case "time":
          return (a.checkout_time || "").localeCompare(b.checkout_time || "");
        case "count":
          return b.animals.length - a.animals.length;
        default:
          return 0;
      }
    });
    
    return filtered;
  }, [ownerGroups, searchTerm, sortBy, sizeFilter]);

  const fetchGoingHome = async (force = false) => {
    try {
      const url = force ? `${API}/going-home?force=true` : `${API}/going-home`;
      const response = await axios.get(url);
      setAnimals(response.data.animals || []);
      setLastSynced(response.data.last_synced);
      setGingrConfigured(response.data.gingr_configured !== false);
      setGingrMessage(response.data.message || "");
      setSmallCount(response.data.small_count || 0);
      setLargeCount(response.data.large_count || 0);
      setPrivatePlayCount(response.data.private_play_count || 0);
      setDayBoardingCount(response.data.day_boarding_count || 0);
      setStandingCount(response.data.standing_count || 0);
      // SWR: cache for instant display on next visit
      try { localStorage.setItem(`swr_goinghome_${ghLocId}`, JSON.stringify(response.data)); } catch(e) {}
    } catch (error) {
      console.error("Going home fetch failed:", error.response?.status);
      if (error.response?.status === 503 || error.response?.status === 500 || !error.response) {
        if (animals.length === 0) {
          setGingrConfigured(false);
          setGingrMessage("Unable to connect. Data will load automatically when available.");
        }
      }
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  };

  const handleRefresh = () => {
    setSyncing(true);
    fetchGoingHome(true);  // Force fresh fetch from Gingr API
  };

  // One-click notify - directly notifies daycare without opening modal
  const notifyDaycare = async (ownerGroup, e) => {
    e.stopPropagation(); // Prevent card click
    setNotifying(ownerGroup.owner_key || ownerGroup.owner_id || ownerGroup.owner_name);
    
    try {
      // Send all animals for this owner (including image_url for TV display)
      const response = await axios.post(`${API}/going-home/notify`, {
        animals: ownerGroup.animals.map(animal => ({
          animal_id: animal.animal_id,
          animal_name: animal.animal_name,
          breed: animal.breed,
          color: animal.color,
          reservation_type: animal.reservation_type,
          image_url: animal.image_url,  // Include dog photo for TV display
          room: animal.room  // Room assignment from Gingr playgroup flag
        })),
        owner_name: ownerGroup.owner_name,
        owner_id: ownerGroup.owner_id,
        checkout_time: ownerGroup.checkout_time,
        // For single dog, also include image_url at top level for backwards compatibility
        image_url: ownerGroup.animals.length === 1 ? ownerGroup.animals[0].image_url : null
      });
      
      const dogNames = ownerGroup.animals.map(a => a.animal_name).join(" & ");
      
      // Notification is ALWAYS saved and will reach the TV via polling
      // So always show success
      const delivery = response.data?.delivery;
      
      if (delivery && delivery.messages_sent > 0) {
        toast.success(`Daycare notified: ${dogNames} going home!`, {
          duration: 3000,
          description: `Sent to TV display instantly`
        });
      } else {
        toast.success(`Daycare notified: ${dogNames} going home!`, {
          duration: 3000,
          description: delivery?.tv_displays_active > 0 
            ? "TV will show notification within seconds" 
            : "Notification saved for when TV connects"
        });
      }
    } catch (error) {
      console.error('Failed to notify daycare:', error);
      
      // Provide helpful error messages
      if (error.response?.status === 401) {
        toast.error("Session expired - please refresh the page", { duration: 5000 });
      } else if (error.response?.status === 403) {
        toast.error("Access denied - you don't have permission for this location", { duration: 5000 });
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail, { duration: 5000 });
      } else if (!navigator.onLine) {
        toast.error("No internet connection", { duration: 5000 });
      } else {
        toast.error("Failed to notify daycare - please try again", { duration: 5000 });
      }
    } finally {
      setNotifying(null);
    }
  };

  // Send custom message to daycare
  const sendMessageToDaycare = async () => {
    if (!customMessage.trim()) {
      toast.error("Please enter a message");
      return;
    }
    
    setSendingMessage(true);
    try {
      // Build rooms array based on target
      const rooms = messageTarget === "both" ? [] : [messageTarget];
      const targetLabel = messageTarget === "both" ? "all rooms" : `${messageTarget} dog room`;
      
      const response = await axios.post(`${API}/going-home/message`, {
        message: customMessage.trim(),
        rooms: rooms
      });
      
      // Check delivery status
      const delivery = response.data?.delivery;
      const warning = response.data?.warning;
      
      if (delivery && delivery.messages_sent > 0) {
        toast.success(`Message sent to ${targetLabel}!`, { 
          duration: 4000,
          description: `Delivered to ${delivery.messages_sent} TV display(s)`
        });
      } else if (warning) {
        toast.info(`Message saved for ${targetLabel}. TV will receive it shortly.`, {
          duration: 4000,
          description: "TV may be briefly reconnecting."
        });
      } else {
        toast.success(`Message sent to daycare!`, { duration: 4000 });
      }
      
      setCustomMessage("");
      setMessageModalOpen(false);
      setMessageTarget("both");
    } catch (error) {
      console.error('Failed to send message:', error);
      
      // Provide helpful error messages based on the error type
      if (error.response?.status === 401) {
        toast.error("Session expired - please refresh and log in again", { duration: 5000 });
      } else if (error.response?.status === 403) {
        toast.error("Access denied - you don't have permission for this location", { duration: 5000 });
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail, { duration: 5000 });
      } else if (!navigator.onLine) {
        toast.error("No internet connection - please check your network", { duration: 5000 });
      } else {
        toast.error("Failed to send message - please try again", { duration: 5000 });
      }
    } finally {
      setSendingMessage(false);
    }
  };

  // Count unique owners
  const uniqueOwners = ownerGroups.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 animate-spin mx-auto text-blue-600 mb-4" />
          <p className="text-lg text-gray-600">Loading checkout data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ops-v3 min-h-screen p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-3xl sm:text-[32px] font-extrabold tracking-tight text-gray-900 mb-1" style={{ fontFamily: 'Manrope' }}>
              Going Home Board
            </h1>
            <p className="text-sm sm:text-base text-gray-500">Dogs checking out today</p>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {/* TV Connection Status Indicator */}
            {tvConnected === null ? (
              <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-300">
                <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                Checking TV...
              </Badge>
            ) : tvConnected ? (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                <Tv className="h-3 w-3 mr-1" />
                {tvCount} TV Connected
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">
                <AlertCircle className="h-3 w-3 mr-1" />
                No TV Connected
              </Badge>
            )}
            {/* TV Display Mode - Room selector (tap to open; big targets for TVs/touch) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-purple-50 border-purple-300 hover:bg-purple-100"
                  data-testid="tv-display-btn"
                >
                  <Tv className="h-4 w-4 mr-2" />
                  TV Display
                  <ChevronDown className="h-4 w-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={6} className="w-64 p-2" data-testid="tv-display-menu">
                {[
                  { room: "large", label: "Large Dog Room", dot: "bg-emerald-500", hover: "focus:bg-emerald-50", tid: "tv-open-large" },
                  { room: "small", label: "Small Dog Room", dot: "bg-blue-500", hover: "focus:bg-blue-50", tid: "tv-open-small" },
                  { room: null, label: "All Rooms", dot: "bg-purple-500", hover: "focus:bg-purple-50", tid: "tv-open-all" },
                ].map((o) => (
                  <DropdownMenuItem
                    key={o.tid}
                    onSelect={() => {
                      const qs = new URLSearchParams();
                      if (o.room) qs.set("room", o.room);
                      if (currentLocationId) qs.set("location", currentLocationId);
                      const q = qs.toString();
                      window.open(`/going-home/display${q ? `?${q}` : ""}`, "_blank");
                    }}
                    className={`cursor-pointer px-4 py-4 text-base font-semibold gap-3 rounded-lg ${o.hover}`}
                    data-testid={o.tid}
                  >
                    <span className={`w-4 h-4 rounded-full ${o.dot}`} />
                    {o.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              onClick={handleRefresh}
              disabled={syncing}
              size="sm"
              className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? "Syncing..." : "Refresh"}
            </Button>
          </div>
        </div>

        {/* Stats Cards - Modern Gradient Style */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-4">
          <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0 shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-xs font-medium">Total Dogs</p>
                  <p className="text-3xl font-bold mt-1">{animals.length}</p>
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <DogIcon className="w-5 h-5" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`border-0 shadow-lg cursor-pointer transition-all ${sizeFilter === 'small' ? 'ring-4 ring-cyan-300 scale-105' : 'hover:scale-102 hover:shadow-xl'} bg-gradient-to-br from-cyan-500 to-blue-600 text-white v3-grp-sm`}
                onClick={() => setSizeFilter(sizeFilter === 'small' ? 'all' : 'small')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-cyan-100 text-xs font-medium">Small Dogs</p>
                  <p className="text-3xl font-bold mt-1">{smallCount}</p>
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Scale className="w-5 h-5" />
                </div>
              </div>
              <p className="text-cyan-100 text-xs mt-1">&lt;35 lb</p>
            </CardContent>
          </Card>

          <Card className={`border-0 shadow-lg cursor-pointer transition-all ${sizeFilter === 'large' ? 'ring-4 ring-emerald-300 scale-105' : 'hover:scale-102 hover:shadow-xl'} bg-gradient-to-br from-emerald-500 to-green-600 text-white v3-grp-lg`}
                onClick={() => setSizeFilter(sizeFilter === 'large' ? 'all' : 'large')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-xs font-medium">Large Dogs</p>
                  <p className="text-3xl font-bold mt-1">{largeCount}</p>
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Scale className="w-5 h-5" />
                </div>
              </div>
              <p className="text-emerald-100 text-xs mt-1">≥36 lb</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-rose-500 to-red-600 text-white border-0 shadow-lg v3-grp-pp">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-rose-100 text-xs font-medium">Private Play</p>
                  <p className="text-3xl font-bold mt-1">{privatePlayCount}</p>
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <DogIcon className="w-5 h-5" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white border-0 shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-xs font-medium">Day Boarding</p>
                  <p className="text-3xl font-bold mt-1">{dayBoardingCount}</p>
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Clock className="w-5 h-5" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white border-0 shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-xs font-medium">Owners</p>
                  <p className="text-3xl font-bold mt-1">{uniqueOwners}</p>
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Users className="w-5 h-5" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white border-0 shadow-lg" data-testid="standing-kpi-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-violet-100 text-xs font-medium">Standing</p>
                  <p className="text-3xl font-bold mt-1">{standingCount}</p>
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Repeat className="w-5 h-5" />
                </div>
              </div>
              <p className="text-violet-100 text-xs mt-1">Recurring booking</p>
            </CardContent>
          </Card>
        </div>

        {/* Single Row: Toggle, Search, Sort, Last Synced */}
        <div className="flex flex-col md:flex-row items-center gap-3 mb-6 bg-white/50 backdrop-blur-sm rounded-lg p-3 border border-gray-100">
          {/* Toggle for Private Play and Day Boarding */}
          {(privatePlayCount > 0 || dayBoardingCount > 0) && (
            <Button
              variant={showSpecialCategories ? "default" : "outline"}
              size="sm"
              onClick={() => setShowSpecialCategories(!showSpecialCategories)}
              className={`whitespace-nowrap ${showSpecialCategories ? "bg-red-500 hover:bg-red-600" : "border-red-300 text-red-700 hover:bg-red-50"}`}
              data-testid="toggle-special-categories"
            >
              {showSpecialCategories ? "Hide" : "Show"} Private Play & Day Boarding ({privatePlayCount + dayBoardingCount})
            </Button>
          )}
          
          {/* Search */}
          <div className="flex-1 relative w-full md:w-auto">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by dog name, breed, or owner..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-9"
            />
          </div>
          
          {/* Sort */}
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-full md:w-48 h-9">
              <SortAsc className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Sort by..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="owner">Owner Name (A-Z)</SelectItem>
              <SelectItem value="name">Dog Name (A-Z)</SelectItem>
              <SelectItem value="time">Pickup Time</SelectItem>
              <SelectItem value="count">Number of Dogs</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Last Synced */}
          <div className="flex items-center gap-2 text-sm text-gray-500 whitespace-nowrap">
            <Clock className="h-4 w-4" />
            <span>Synced: {lastSynced ? new Date(lastSynced).toLocaleTimeString() : 'Never'}</span>
          </div>
        </div>

        {/* Owner Groups Grid - Pet-focused cards */}
        {ownerGroups.length > 0 && <LeavingSoon groups={ownerGroups} />}
        {filteredGroups.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Send Message Card - Always first */}
            <Card 
              onClick={() => setMessageModalOpen(true)}
              className="keep-color overflow-hidden cursor-pointer border-0 transition-transform hover:-translate-y-0.5 text-white"
              style={{ background: "linear-gradient(135deg, #1F8A7C 0%, #2BA597 100%)", boxShadow: "0 10px 24px -12px rgba(31,138,124,0.55)" }}
              data-testid="send-message-card"
            >
              <CardContent className="p-6 flex flex-col items-center justify-center min-h-[200px]">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: "rgba(255,255,255,0.22)" }}>
                  <MessageSquare className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-extrabold text-white text-xl text-center mb-2">
                  Send Message
                </h3>
                <p className="text-sm text-white/85 text-center">
                  Send a custom message to daycare TV
                </p>
              </CardContent>
            </Card>

            {filteredGroups.map((group) => (
              <Card 
                key={group.owner_key || group.owner_id || group.owner_name}
                className={`overflow-hidden hover:shadow-xl bg-white border-2 transition-all ${
                  group.animals.length > 1 ? 'border-orange-400 ring-2 ring-orange-200' : 'border-gray-200 hover:border-blue-400'
                }`}
                data-testid={`owner-card-${group.owner_id || group.owner_name.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {/* Dogs Section - TOP (Pet name prominently displayed) */}
                <CardContent className="p-4 space-y-3">
                  <PackageRenewalBanner status={packageMap[String(group.owner_id)]} />
                  {group.animals.map((animal, idx) => (
                    <div key={animal.reservation_id || idx} className={`p-3 rounded-xl border ${
                      animal.size_category === 'small' 
                        ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200' 
                        : animal.size_category === 'large'
                        ? 'bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200'
                        : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100'
                    }`}>
                      <div className="flex items-start gap-3">
                        {/* Dog Photo or Icon */}
                        {animal.image_url ? (
                          <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 shadow-md border-2 border-white">
                            <img 
                              src={animal.image_url} 
                              alt={animal.animal_name}
                              className="w-full h-full object-cover"
                              onError={(e) => { 
                                e.target.style.display = 'none'; 
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div className={`w-full h-full hidden items-center justify-center ${
                              animal.size_category === 'small'
                                ? 'bg-gradient-to-br from-blue-400 to-blue-500'
                                : animal.size_category === 'large'
                                ? 'bg-gradient-to-br from-green-400 to-emerald-500'
                                : 'bg-gradient-to-br from-gray-400 to-gray-500'
                            }`}>
                              <DogIcon className="w-7 h-7 text-white" />
                            </div>
                          </div>
                        ) : (
                          <div className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 shadow-md ${
                            animal.size_category === 'small'
                              ? 'bg-gradient-to-br from-blue-400 to-blue-500'
                              : animal.size_category === 'large'
                              ? 'bg-gradient-to-br from-green-400 to-emerald-500'
                              : 'bg-gradient-to-br from-gray-400 to-gray-500'
                          }`}>
                            <DogIcon className="w-7 h-7 text-white" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          {/* Pet Name - LARGE */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-bold text-gray-900 text-xl leading-tight">
                              {animal.animal_name}
                            </h3>
                            <StandingBadge isStanding={animal.is_standing} size="xs" />
                            {/* Size Badge */}
                            {animal.size_category && (
                              <Badge className={`text-xs ${
                                animal.size_category === 'small' 
                                  ? 'bg-blue-500 text-white' 
                                  : 'bg-green-500 text-white'
                              }`}>
                                {animal.size_category === 'small' ? '🐕 S' : '🐕‍🦺 L'}
                              </Badge>
                            )}
                          </div>
                          {/* Breed */}
                          {animal.breed && (
                            <p className="text-sm text-gray-600 font-medium">
                              {animal.breed}
                            </p>
                          )}
                          {/* Weight and Additional Info Row */}
                          <div className="flex flex-wrap gap-2 mt-1">
                            {animal.weight && (
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                                animal.size_category === 'small'
                                  ? 'text-blue-700 bg-blue-100'
                                  : animal.size_category === 'large'
                                  ? 'text-green-700 bg-green-100'
                                  : 'text-gray-600 bg-gray-100'
                              }`}>
                                <Scale className="w-3 h-3 inline mr-1" />
                                {animal.weight} lb
                              </span>
                            )}
                            {animal.sex && (
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                {animal.sex}
                              </span>
                            )}
                            {animal.reservation_type && (
                              <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded">
                                {animal.reservation_type}
                              </span>
                            )}
                          </div>
                          {/* Gingr Custom Icons/Flags (Playgroups, etc.) */}
                          {animal.icons && animal.icons.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2" data-testid="gingr-icons-container">
                              {animal.icons.map((icon, iconIdx) => (
                                <GingrIcon key={iconIdx} icon={icon} />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Multi-dog indicator */}
                  {group.animals.length > 1 && (
                    <div className="text-center">
                      <Badge className="bg-orange-500 text-white">
                        {group.animals.length} dogs from same owner
                      </Badge>
                    </div>
                  )}
                </CardContent>

                {/* Owner & Time Section - BOTTOM */}
                <div className="px-4 pb-2 border-t border-gray-100 bg-gray-50">
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-2 text-gray-600">
                      <User className="h-4 w-4" />
                      <span className="text-sm font-medium truncate max-w-[120px]">{group.owner_name}</span>
                    </div>
                    {group.checkout_time && group.checkout_time !== "00:00" ? (
                      <div className="flex items-center gap-1 text-gray-500 text-sm">
                        <Clock className="h-3 w-3" />
                        <span>{group.checkout_time}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-gray-400 text-xs">
                        <Clock className="h-3 w-3" />
                        <span>Today</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* One-Click Notify Button */}
                <div className="px-4 pb-4 bg-gray-50">
                  <Button
                    onClick={(e) => notifyDaycare(group, e)}
                    disabled={notifying === (group.owner_key || group.owner_id || group.owner_name)}
                    className={`w-full font-semibold ${
                      group.animals.length > 1 
                        ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600' 
                        : 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600'
                    } text-white`}
                    data-testid={`notify-btn-${group.owner_id || group.owner_name.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {notifying === (group.owner_key || group.owner_id || group.owner_name) ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Notifying...
                      </>
                    ) : (
                      <>
                        <Bell className="h-4 w-4 mr-2" />
                        Notify Daycare
                        {group.animals.length > 1 && ` (${group.animals.length})`}
                      </>
                    )}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        ) : !gingrConfigured ? (
          /* Gingr Not Configured State */
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Send Message Card - Always available */}
            <Card 
              onClick={() => setMessageModalOpen(true)}
              className="keep-color overflow-hidden cursor-pointer border-0 transition-transform hover:-translate-y-0.5 text-white"
              style={{ background: "linear-gradient(135deg, #1F8A7C 0%, #2BA597 100%)", boxShadow: "0 10px 24px -12px rgba(31,138,124,0.55)" }}
              data-testid="send-message-card-empty"
            >
              <CardContent className="p-6 flex flex-col items-center justify-center min-h-[200px]">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: "rgba(255,255,255,0.22)" }}>
                  <MessageSquare className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-extrabold text-white text-xl text-center mb-2">
                  Send Message
                </h3>
                <p className="text-sm text-white/85 text-center">
                  Send a custom message to daycare TV
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-dashed border-amber-300 bg-amber-50 col-span-1 sm:col-span-1 md:col-span-2 lg:col-span-3">
              <CardContent className="pt-12 pb-12 text-center">
                <Settings className="w-16 h-16 text-amber-500 mx-auto mb-4" />
                <p className="text-xl font-semibold text-amber-700 mb-2">Gingr Not Configured</p>
                <p className="text-sm text-amber-600 mb-4">
                  The Going Home board requires Gingr integration to be set up for this location.
                </p>
                <p className="text-xs text-amber-500">
                  Contact your administrator to configure Gingr API for this location.
                </p>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Send Message Card - Show even when no dogs */}
            <Card 
              onClick={() => setMessageModalOpen(true)}
              className="keep-color overflow-hidden cursor-pointer border-0 transition-transform hover:-translate-y-0.5 text-white"
              style={{ background: "linear-gradient(135deg, #1F8A7C 0%, #2BA597 100%)", boxShadow: "0 10px 24px -12px rgba(31,138,124,0.55)" }}
              data-testid="send-message-card-empty"
            >
              <CardContent className="p-6 flex flex-col items-center justify-center min-h-[200px]">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: "rgba(255,255,255,0.22)" }}>
                  <MessageSquare className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-extrabold text-white text-xl text-center mb-2">
                  Send Message
                </h3>
                <p className="text-sm text-white/85 text-center">
                  Send a custom message to daycare TV
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-dashed border-gray-300 col-span-1 sm:col-span-1 md:col-span-2 lg:col-span-3">
              <CardContent className="pt-12 pb-12 text-center">
                <DogIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-xl font-semibold text-gray-600 mb-2">No Dogs Checking Out Today</p>
                <p className="text-sm text-gray-500">Check back later or refresh to see updates</p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Message Modal */}
      <Dialog open={messageModalOpen} onOpenChange={(open) => { setMessageModalOpen(open); if (!open) { setCustomMessage(""); setMessageTarget("both"); } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              Send Message to Daycare
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Room Target Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Send to
              </label>
              <div className="grid grid-cols-3 gap-2" data-testid="message-room-selector">
                <button
                  onClick={() => setMessageTarget("large")}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                    messageTarget === "large" 
                      ? "bg-emerald-500 text-white border-emerald-500 shadow-md" 
                      : "bg-white text-gray-700 border-gray-200 hover:border-emerald-300 hover:bg-emerald-50"
                  }`}
                  data-testid="message-target-large"
                >
                  Large Room
                </button>
                <button
                  onClick={() => setMessageTarget("small")}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                    messageTarget === "small" 
                      ? "bg-blue-500 text-white border-blue-500 shadow-md" 
                      : "bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:bg-blue-50"
                  }`}
                  data-testid="message-target-small"
                >
                  Small Room
                </button>
                <button
                  onClick={() => setMessageTarget("both")}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                    messageTarget === "both" 
                      ? "bg-purple-500 text-white border-purple-500 shadow-md" 
                      : "bg-white text-gray-700 border-gray-200 hover:border-purple-300 hover:bg-purple-50"
                  }`}
                  data-testid="message-target-both"
                >
                  Both Rooms
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Message
              </label>
              <Textarea
                placeholder="Type your message here... (e.g., 'Please bring Max to the front for medication')"
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                className="min-h-[120px] resize-none"
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1 text-right">
                {customMessage.length}/500 characters
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> This message will appear on {messageTarget === "both" ? "all daycare" : `the ${messageTarget} dog room`} TV display{messageTarget === "both" ? "s" : ""}.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setMessageModalOpen(false);
                  setCustomMessage("");
                  setMessageTarget("both");
                }}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-semibold"
                onClick={sendMessageToDaycare}
                disabled={sendingMessage || !customMessage.trim()}
                data-testid="send-message-btn"
              >
                {sendingMessage ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send to {messageTarget === "both" ? "Both" : messageTarget === "large" ? "Large" : "Small"}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default GoingHome;
